import { useState, useEffect } from "react";
import {
  ArrowsPointingOutIcon,
  ArrowDownIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { GenericImage } from "./GenericImage";
import { IAttachment } from "../types/IAttachment";
import { UtilService } from "../Utils/UtilService";
import { PdfViewer } from "./PdfViewer";

interface GenericZoomFileProps {
  src: IAttachment;
  height?: number;
  width?: number;
}

export const GenericZoomFile = ({
  src,
  height = 70,
  width = 70,
}: GenericZoomFileProps) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [numPages, setNumPages] = useState<number>(0);
  const [fileUrl, setFileUrl] = useState<string>("");

  const isPdf = src?.extension?.toLowerCase().includes("pdf");

  useEffect(() => {
    if (src?.file && src?.extension) {
      const url = UtilService.base64ToAttachment(src.file, src.extension);
      setFileUrl(url);

      return () => URL.revokeObjectURL(url);
    }
  }, [src?.file, src?.extension]);

  // Move this check after hooks
  if (!src) {
    return <GenericImage width={width} height={height} />;
  }

  if (!fileUrl) return <div>Loading...</div>;

  return (
    <>
      <div className="relative group w-fit">
        {isPdf ? (
          <div className="overflow-hidden" style={{ height, width }}>
            <PdfViewer
              fileUrl={fileUrl}
              numPages={numPages}
              setNumPages={setNumPages}
              width={width}
            />
          </div>
        ) : (
          <GenericImage src={fileUrl} width={width} height={height} />
        )}

        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => setIsZoomed(true)}
            className="text-white bg-primary-700 px-4 py-2 rounded hover:bg-primary-800"
          >
            <ArrowsPointingOutIcon className="h-4 w-4" />
          </button>
          <a
            href={fileUrl}
            download={src.name || "file"}
            className="text-white bg-primary-700 px-4 py-2 rounded ml-2 hover:bg-primary-800"
          >
            <ArrowDownIcon className="h-4 w-4" />
          </a>
        </div>
      </div>

      {isZoomed && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center">
          <button
            className="absolute top-10 right-10 text-white bg-blue-800 rounded-xl"
            onClick={() => setIsZoomed(false)}
          >
            <XMarkIcon className="h-6 w-6 m-4" />
          </button>

          {isPdf ? (
            <div className="bg-white p-4 max-h-[90vh] overflow-auto">
              <PdfViewer
                fileUrl={fileUrl}
                numPages={numPages}
                setNumPages={setNumPages}
                zoomed
              />
            </div>
          ) : (
            <GenericImage
              src={fileUrl}
              height={500}
              width={500}
              className="max-w-full max-h-full"
            />
          )}
        </div>
      )}
    </>
  );
};
